import { useEffect, useReducer, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "react-bootstrap";
import Select from "react-select";
import { optionSelector } from "../../../../config/apiSPA";
/**
 * @typedef {Object} scoreTestData
 * @property {number} tel2CompetencyId
 * @property {number} soalEventId
 * @property {string} testType
 * @property {string} attemptSelection
 */
/**
 * @param {{
 * show: boolean,
 * onHide: () => void,
 * title?: string,
 * data?: scoreTestData,
 * submitClick: (data: scoreTestData) => void
 * }} param0
 * @returns {JSX.Element}
 */
function ModalImportTestResult({ show, onHide, title = "Import Test", data, submitClick }) {
  const [formData, setFormData] = useState({
    tel2CompetencyId: data?.tel2CompetencyId ?? null,
    soalEventId: data?.soalEventId ?? null,
    testType: data?.testType ?? "post",
    attemptSelection: data?.attemptSelection ?? "first_attempt",
  });

  const [testEventOptions, setCompetencyOptions] = useState([]);
  const [isLoadingTestEvent, setIsLoadingTestEvent] = useState(false);

  // Reset form ketika modal dibuka / data berubah
  useEffect(() => {
    if (!show) return;

    setFormData({
      tel2CompetencyId: data?.tel2CompetencyId ?? null,
      soalEventId: data?.soalEventId ?? null,
      testType: data?.testType ?? "post",
      attemptSelection: data?.attemptSelection ?? "first_attempt",
    });
  }, [show, data]);

  // Load competency
  useEffect(() => {
    if (!show) return;

    const loadCompetency = async () => {
      try {
        setIsLoadingTestEvent(true);

        const res = await optionSelector("/teory-test-list");

        setCompetencyOptions(res);
      } catch (error) {
        console.error("Failed to load competency:", error);
        setCompetencyOptions([]);
      } finally {
        setIsLoadingTestEvent(false);
      }
    };

    loadCompetency();
  }, [show]);

  const handleChangeTestType = (e) => {
    setFormData((prev) => ({
      ...prev,
      testType: e.target.value,
    }));
  };

  const handleTestEventChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      soalEventId: selectedOption?.value ?? null,
    }));
  };
  const handleChangeAttempt = (e) => {
    setFormData((prev) => ({
      ...prev,
      attemptSelection: e.target.value,
    }));
  };

  const handleClickSubmit = () => {
    if (!isFormValid) return;

    submitClick({
      tel2CompetencyId: formData.tel2CompetencyId,
      soalEventId: formData.soalEventId,
      testType: formData.testType,
      attemptSelection: formData.attemptSelection,
    });
  };

  const isFormValid = formData.tel2CompetencyId !== null && formData.soalEventId !== null && formData.testType !== null && formData.attemptSelection !== null;

  const selectedCompetency = testEventOptions.find((option) => option.value === formData.soalEventId) ?? null;

  return (
    <Modal show={show} onHide={onHide}>
      <ModalHeader onHide={onHide}>{title}</ModalHeader>
      <ModalBody>
        {/* Competency */}
        <div className="form-group mb-3">
          <label className="form-label" htmlFor="competency">
            Test Event
          </label>

          <Select
            inputId="competency"
            classNamePrefix="react-select"
            isSearchable
            isClearable
            isLoading={isLoadingTestEvent}
            isDisabled={isLoadingTestEvent}
            options={testEventOptions}
            value={selectedCompetency}
            onChange={handleTestEventChange}
            placeholder={isLoadingTestEvent ? "Loading test event..." : "Select test event..."}
            noOptionsMessage={() => "test event not found"}
          />
        </div>

        {/* import to */}
        <div className="form-group mb-3">
          <label className="form-label">Import To</label>

          <div className="d-flex gap-3">
            <div className="form-check">
              <input type="radio" className="form-check-input" id="pre" name="testType" value="pre" checked={formData.testType === "pre"} onChange={handleChangeTestType} />
              <label className="form-check-label" htmlFor="pre">
                Pre Test
              </label>
            </div>

            <div className="form-check">
              <input type="radio" className="form-check-input" id="post" name="testType" value="post" checked={formData.testType === "post"} onChange={handleChangeTestType} />
              <label className="form-check-label" htmlFor="post">
                Post Test
              </label>
            </div>
          </div>
        </div>
        {/* attemptSelection selection */}
        <div className="form-group mb-3">
          <label className="form-label">Attempt Selection</label>

          <div className="d-flex gap-3">
            <div className="form-check">
              <input type="radio" className="form-check-input" id="first" name="attemptSelection" value="first_attempt" checked={formData.attemptSelection === "first_attempt"} onChange={handleChangeAttempt} />
              <label className="form-check-label" htmlFor="first">
                First
              </label>
            </div>

            <div className="form-check">
              <input type="radio" className="form-check-input" id="latest" name="attemptSelection" value="latest_attempt" checked={formData.attemptSelection === "latest_attempt"} onChange={handleChangeAttempt} />
              <label className="form-check-label" htmlFor="latest">
                Last
              </label>
            </div>
            <div className="form-check">
              <input type="radio" className="form-check-input" id="high" name="attemptSelection" value="highest_score" checked={formData.attemptSelection === "highest_score"} onChange={handleChangeAttempt} />
              <label className="form-check-label" htmlFor="high">
                High Score
              </label>
            </div>
          </div>
        </div>
        <hr />

        <div className="d-flex align-items-start gap-2 mt-3">
          <i className="fa fa-info-circle text-info mt-1"></i>
          <small className="text-muted">Only test events with "Share to All" disabled are available for import.(sings)</small>
        </div>
      </ModalBody>

      <ModalFooter>
        <button onClick={onHide} type="button" className="btn btn-secondary">
          Cancel
        </button>

        <button onClick={handleClickSubmit} type="button" className="btn btn-outline-theme" disabled={!isFormValid}>
          Import Scores
        </button>
      </ModalFooter>
    </Modal>
  );
}

export default ModalImportTestResult;
